import { isEqual } from 'lodash-es';

export const enum DataBindingPartType {
    Advanced,
    Index,
    Simple,
}

export abstract class DataBindingPart {
    readonly value: string | number | (string | null)[] = '';

    protected constructor(readonly type: DataBindingPartType) {}

    abstract toString(): string;

    isAdvanced(): this is AdvancedDataBindingPart {
        return this.type === DataBindingPartType.Advanced;
    }

    isIndex(): this is IndexDataBindingPart {
        return this.type === DataBindingPartType.Index;
    }

    isSimple(): this is SimpleDataBindingPart {
        return this.type === DataBindingPartType.Simple;
    }

    equals(rhs: DataBindingPart): boolean {
        return this.type === rhs.type && isEqual(this.value, rhs.value);
    }
}

export class AdvancedDataBindingPart extends DataBindingPart {
    constructor(readonly value: (string | null)[]) {
        super(DataBindingPartType.Advanced);
    }

    equals(rhs: DataBindingPart): boolean {
        return this.type === rhs.type && isEqual(this.value, rhs.value);
    }

    toString() {
        return this.value.map((v) => v ?? 'null').join('@');
    }
}

export class IndexDataBindingPart extends DataBindingPart {
    constructor(readonly value: number) {
        super(DataBindingPartType.Index);
    }

    toString() {
        return this.value.toString();
    }
}

export class SimpleDataBindingPart extends DataBindingPart {
    constructor(readonly value: string) {
        super(DataBindingPartType.Simple);
    }

    toString() {
        return this.value;
    }
}

export class DataBinding {
    /**
     * Il binding completo.
     */
    readonly full: ReadonlyArray<DataBindingPart>;

    /**
     * Crea un DataBinding vuoto.
     */
    constructor();
    /**
     * Crea un DataBinding effettuando il parsing di una stringa.
     * @param original Il path originale, normalmente come creato dal configuratore.
     * @example a.b@123.c
     * @param context Il contesto superiore relativo al binding.
     * @example [a, 0, b, 1]
     */
    constructor(original: string, context?: DataBinding);
    /**
     * Crea un DataBinding dalle sue componenti primarie.
     * @param parts Le parti che compongono il DataBinding.
     * @example [a, b, '123', c]
     * @param context Il contesto superiore relativo al binding.
     * @example [a, 0, b, 1]
     */
    constructor(parts: DataBindingPart[], context?: DataBinding);
    constructor(originalOrParts?: string | DataBindingPart[], context?: DataBinding) {
        if (!originalOrParts) {
            this.full = [];
            return;
        }

        if (originalOrParts instanceof Array) {
            this.full = DataBinding.applyContext(originalOrParts, context);
            return;
        }

        this.full = DataBinding.applyContext(DataBinding.parse(originalOrParts), context);
    }

    /**
     * Recupera la entity rappresentata da questo binding.
     * @example a.b@123.c -> a.b@123
     */
    get entity() {
        return new DataBinding(this.full.slice(0, -1));
    }

    /**
     * Recupera il binding a meno della root.
     * @example a.b@123.c -> b@123.c
     */
    get next() {
        return new DataBinding(this.full.slice(1));
    }

    /**
     * Recupera la proprietà finale di questo binding.
     * @example a.b@123.c -> c
     */
    get property() {
        return this.full[this.full.length - 1];
    }

    /**
     * Recupera la proprietà finale di questo binding.
     * @example a.b@123.c -> c
     */
    get propertyName(): string {
        const prop = this.property;
        if (!prop || prop.isAdvanced()) {
            return '';
        }
        return prop.value.toString();
    }

    getFirstAdvancedItemId(): string {
        const advanced = this.advanced;
        const first = advanced[0];
        if (!first) return '';
        return Array.isArray(first.value) ? first.value[0] || '' : String(first.value);
    }

    /**
     * Recupera la root di questo binding.
     * @example a.b@123.c -> a.b@123
     */
    get root() {
        return this.full[0];
    }

    /**
     * Recupera il binding a meno delle parti avanzate.
     * @example a.b@123.c -> a.b.c
     */
    get simple() {
        return this.full.filter((part) => part.isSimple());
    }

    get advanced() {
        return this.full.filter((part) => part.isAdvanced());
    }

    /**
     * True se il binding contiene almeno una parte avanzata.
     */
    isAdvanced(): boolean {
        return this.advanced.length > 0;
    }

    /**
     * Aggiungi una parte in coda al DataBinding.
     * @param part La parte da aggiungere.
     * @returns Un nuovo DataBinding.
     */
    add(part: DataBindingPart) {
        return new DataBinding(this.full.concat(part));
    }

    /**
     * Aggiungi una parte avanzata in coda al DataBinding.
     * @param parts La parte da aggiungere.
     * @returns Un nuovo DataBinding.
     */
    addAdvanced(...parts: (string | null)[]) {
        if (this.full.length > 0) {
            const last = this.full[this.full.length - 1];
            // Se l'ultima parte del databinding è già avanzata, aggiorna quella, invece che aggiungere una nuova parte.
            if (last.isAdvanced()) {
                const newParts = this.full.slice();
                newParts[newParts.length - 1] = new AdvancedDataBindingPart(last.value.concat(parts));
                return new DataBinding(newParts);
            }
        }
        return new DataBinding(this.full.concat(new AdvancedDataBindingPart(parts)));
    }

    /**
     * Aggiungi una parte indice in coda al DataBinding.
     * @param index La parte da aggiungere.
     * @returns Un nuovo DataBinding.
     */
    addIndex(index: number) {
        return new DataBinding(this.full.concat(new IndexDataBindingPart(index)));
    }

    /**
     * Aggiungi una parte semplice in coda al DataBinding.
     * @param part La parte da aggiungere.
     * @returns Un nuovo DataBinding.
     */
    addSimple(part: string) {
        return new DataBinding(this.full.concat(new SimpleDataBindingPart(part)));
    }

    /**
     * Controlla se due DataBinding sono uguali.
     * @param lhs Il secondo binding da comparare.
     * @returns Se tutte le parti dei due binding sono uguali.
     */
    equals(lhs?: DataBinding) {
        if (this === lhs) {
            return true;
        }
        if (!lhs || lhs.full.length !== this.full.length) {
            return false;
        }

        for (let i = 0; i < this.full.length; ++i) {
            if (!this.full[i].equals(lhs.full[i])) {
                return false;
            }
        }

        return true;
    }

    /**
     * Controlla se un DataBinding è vuoto.
     * @returns Se il binding non contiene parti.
     */
    isEmpty() {
        return this.full.length === 0;
    }

    /**
     * Effettua il parse inverso del binding.
     * @returns Una stringa che descrive il binding.
     */
    toString() {
        let result = '';
        for (const part of this.full) {
            if (result && part.isAdvanced()) {
                result += '@' + part.toString();
            } else if (result) {
                result += '.' + part.toString();
            } else {
                result += part.toString();
            }
        }

        return result;
    }

    /**
     * Recupera il binding a meno delle parti avanzate.
     * @example a.b@123.c -> a.b.c
     */
    toStringSimple() {
        return this.simple.map((part) => part.value).join('.');
    }

    toStringForNotification(): string {
        const parts = this.full.filter((p) => !p.isIndex());
        if (this.property?.isIndex()) {
            parts.push(this.property);
        }
        return new DataBinding(parts).toString();
    }

    static addParentIndex(parentBinding: DataBinding, childBinding: DataBinding): DataBinding {
        const fullParent = parentBinding.full;
        let fullChild = [...childBinding.full];

        let index = 0;
        while (index < fullParent.length) {
            const parentIsIndex = fullParent[index].isIndex();
            const childIsIndex = fullChild[index]?.isIndex();//fullChild[index]?.isIndex() ?? false;
            if (parentIsIndex && !childIsIndex) {
                fullChild.splice(index, 0, new IndexDataBindingPart(fullParent[index].value as number));
            } else if (parentIsIndex && childIsIndex) {
                const parentIndexValue = fullParent[index].value;
                const childIndexValue = fullChild[index].value;
                if (parentIndexValue !== childIndexValue) {
                    fullChild[index] = new IndexDataBindingPart(parentIndexValue as number);
                }
            } else if (!parentIsIndex && childIsIndex) {
                //error!!!
                console.error('unexpected index at position' + index, parentBinding, childBinding);
            }

            index++;
        }

        return new DataBinding(fullChild);
    }

    /**
     * Applica il contesto alle parti di dataBinding specificate.
     * @param parts Le parti del dataBinding originale.
     * @param context Il contesto da applicare.
     * @example a.b@123.c, a.1 -> a.1.b@123.c
     */
    private static applyContext(parts: DataBindingPart[], context?: DataBinding): DataBindingPart[] {
        if (!context || context.isEmpty()) {
            return parts;
        }

        let iParts = 0;
        let iCtx = 0;
        parts = parts.slice();
        while (iParts < parts.length && iCtx < context.full.length) {
            const part = parts[iParts];
            const ctx = context.full[iCtx];

            if ((ctx.isIndex() && !part.isIndex()) || (ctx.isAdvanced() && !part.isAdvanced())) {
                // Inserisci il contesto
                parts.splice(iParts, 0, ctx);

                ++iParts;
                ++iCtx;
                continue;
            }

            // Prima si lanciava errore se le altre parti non erano uguali tra i due binding.
            // Es. a.b.c, a.1.d.e.f -> non sono compatibili
            // Ora interrompiamo l'applicazione del context. Da testare se OK.
            if (!ctx.equals(part)) {
                return parts;
            }
            ++iCtx;
            ++iParts;
        }

        return parts;
    }

    /**
     * Effettua il parsing di una stringa in parti di binding.
     * @param originalPath Il path di cui effettuare il parsing.
     * @returns La lista di parti che compongono il binding.
     */
    private static parse(originalPath: string): DataBindingPart[] {
        // Esempio: a.b@123-321.c -> {[a, b, '123-321'], c}

        // Parts: [a, b@123-321, c]
        const parts = originalPath.split('.');

        const path: DataBindingPart[] = [];
        for (const part of parts) {
            if (part.indexOf('@') !== -1) {
                const [prop, ...advancedParts] = part.split('@');

                path.push(new SimpleDataBindingPart(prop));
                path.push(new AdvancedDataBindingPart(advancedParts.map((part) => (part === 'null' ? null : part))));
            } else if (!isNaN(+part)) {
                path.push(new IndexDataBindingPart(+part));
            } else {
                path.push(new SimpleDataBindingPart(part));
            }
        }

        return path;
    }
}
